import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Check, X, Star, ThumbsUp, ThumbsDown, Eye, 
  MessageSquare, Users, Target, Heart, TrendingUp,
  Award, AlertCircle, Clock, Zap
} from 'lucide-react';

interface AIOutput {
  id: string;
  title: string;
  content: string;
  promptUsed: string;
  metrics: {
    clarity: number;
    engagement: number;
    professionalism: number;
    effectiveness: number;
  };
  highlights: string[];
  weaknesses: string[];
  audience: string;
  tone: string;
  generatedAt: Date;
}

interface ComparisonResult {
  winner: string;
  reasons: string[];
  score: number;
}

export const AIOutputComparison: React.FC<{
  outputs: AIOutput[];
  title: string;
  description?: string;
  onSelectionComplete: (result: ComparisonResult) => void;
  showPrompts?: boolean;
  enableMetrics?: boolean;
}> = ({ 
  outputs, 
  title, 
  description,
  onSelectionComplete,
  showPrompts = false,
  enableMetrics = true 
}) => {
  const [selectedOutput, setSelectedOutput] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState<Record<string, boolean>>({});
  const [comparisonMode, setComparisonMode] = useState<'quick' | 'detailed'>('quick');
  const [selectionReasons, setSelectionReasons] = useState<string[]>([]);

  const handleOutputSelect = (outputId: string) => {
    setSelectedOutput(outputId);
  };

  const toggleDetails = (outputId: string) => {
    setShowDetails(prev => ({
      ...prev,
      [outputId]: !prev[outputId]
    }));
  };

  const handleReasonToggle = (reason: string) => {
    setSelectionReasons(prev =>
      prev.includes(reason)
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  const submitSelection = () => {
    if (!selectedOutput) return;

    const winner = outputs.find(o => o.id === selectedOutput)!;
    const avgScore = Object.values(winner.metrics).reduce((a, b) => a + b, 0) / 4;

    onSelectionComplete({
      winner: selectedOutput,
      reasons: selectionReasons,
      score: avgScore
    });
  };

  const reasonOptions = [
    { id: 'clarity', label: 'Clearer message', icon: <Eye className="w-4 h-4" /> },
    { id: 'engaging', label: 'More engaging', icon: <Zap className="w-4 h-4" /> },
    { id: 'professional', label: 'Professional tone', icon: <Award className="w-4 h-4" /> },
    { id: 'actionable', label: 'Clear action items', icon: <Target className="w-4 h-4" /> },
    { id: 'audience', label: 'Better for audience', icon: <Users className="w-4 h-4" /> },
    { id: 'emotional', label: 'Emotional connection', icon: <Heart className="w-4 h-4" /> }
  ];

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-bold mb-2">{title}</h2>
        {description && (
          <p className="text-gray-600 text-sm">{description}</p>
        )}
        <div className="flex justify-center mt-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                comparisonMode === 'quick'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setComparisonMode('quick')}
            >
              Quick Compare
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                comparisonMode === 'detailed'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setComparisonMode('detailed')}
            >
              Detailed View
            </button>
          </div>
        </div>
      </div>

      {/* Output Comparison */}
      <div className={`grid gap-4 ${outputs.length === 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
        {outputs.map((output, index) => (
          <motion.div
            key={output.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className={`cursor-pointer transition-all duration-200 ${
              selectedOutput === output.id
                ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200'
                : 'hover:shadow-md border-gray-200'
            }`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{output.title}</CardTitle>
                    {selectedOutput === output.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="text-blue-600"
                      >
                        <Check className="w-5 h-5" />
                      </motion.div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-xs">
                      {output.audience}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {output.tone}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <motion.div
                  className="space-y-4"
                  onClick={() => handleOutputSelect(output.id)}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Content Preview */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-800 line-clamp-4">
                      {output.content}
                    </p>
                    {output.content.length > 200 && (
                      <button
                        className="text-blue-600 text-xs mt-2 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDetails(output.id);
                        }}
                      >
                        {showDetails[output.id] ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {showDetails[output.id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-white p-4 rounded-lg border"
                      >
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {output.content}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Metrics */}
                  {enableMetrics && comparisonMode === 'detailed' && (
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(output.metrics).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <motion.div
                              className="bg-blue-500 h-2 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${value * 10}%` }}
                              transition={{ duration: 0.5, delay: 0.2 }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 w-16 capitalize">
                            {key}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Highlights & Weaknesses */}
                  {comparisonMode === 'detailed' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <h5 className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
                          <ThumbsUp className="w-3 h-3" />
                          Strengths
                        </h5>
                        <ul className="space-y-1">
                          {output.highlights.map((highlight, i) => (
                            <li key={i} className="text-xs text-green-600 flex items-start gap-1">
                              <Check className="w-3 h-3 flex-shrink-0 mt-0.5" />
                              {highlight}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 className="text-xs font-medium text-orange-700 mb-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Areas to Improve
                        </h5>
                        <ul className="space-y-1">
                          {output.weaknesses.map((weakness, i) => (
                            <li key={i} className="text-xs text-orange-600 flex items-start gap-1">
                              <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Prompt Used */}
                  {showPrompts && comparisonMode === 'detailed' && (
                    <details className="bg-gray-50 rounded-lg">
                      <summary className="p-3 cursor-pointer text-sm font-medium text-gray-700">
                        View Prompt Used
                      </summary>
                      <div className="px-3 pb-3">
                        <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                          {output.promptUsed}
                        </pre>
                      </div>
                    </details>
                  )}

                  {/* Quick Actions */}
                  <div className="flex justify-between items-center pt-2">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {new Date(output.generatedAt).toLocaleDateString()}
                    </div>
                    <Button
                      size="sm"
                      variant={selectedOutput === output.id ? 'default' : 'outline'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOutputSelect(output.id);
                      }}
                    >
                      {selectedOutput === output.id ? 'Selected' : 'Select This'}
                    </Button>
                  </div>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Selection Reasoning */}
      <AnimatePresence>
        {selectedOutput && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-800">Why did you choose this option?</CardTitle>
                <p className="text-sm text-blue-600">Select all that apply:</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {reasonOptions.map(reason => (
                    <motion.button
                      key={reason.id}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                        selectionReasons.includes(reason.id)
                          ? 'bg-blue-100 border-blue-300 text-blue-900'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => handleReasonToggle(reason.id)}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className={`flex-shrink-0 ${
                        selectionReasons.includes(reason.id) ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {reason.icon}
                      </div>
                      <span className="text-sm font-medium">{reason.label}</span>
                      {selectionReasons.includes(reason.id) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="ml-auto"
                        >
                          <Check className="w-4 h-4 text-blue-600" />
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
                
                <Button
                  onClick={submitSelection}
                  disabled={selectionReasons.length === 0}
                  className="w-full"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Complete Comparison
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Quick Rating Component for Simple Comparisons
export const QuickRating: React.FC<{
  outputs: AIOutput[];
  onRatingComplete: (ratings: Record<string, number>) => void;
}> = ({ outputs, onRatingComplete }) => {
  const [ratings, setRatings] = useState<Record<string, number>>({});

  const handleRating = (outputId: string, rating: number) => {
    const newRatings = { ...ratings, [outputId]: rating };
    setRatings(newRatings);
    
    if (Object.keys(newRatings).length === outputs.length) {
      onRatingComplete(newRatings);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-center">Rate Each Option</h3>
      {outputs.map(output => (
        <Card key={output.id} className="p-4">
          <div className="mb-3">
            <h4 className="font-medium">{output.title}</h4>
            <p className="text-sm text-gray-600 line-clamp-2">{output.content}</p>
          </div>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map(rating => (
              <motion.button
                key={rating}
                className={`p-2 rounded-full ${
                  ratings[output.id] >= rating
                    ? 'text-yellow-500'
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
                onClick={() => handleRating(output.id, rating)}
                whileTap={{ scale: 1.2 }}
              >
                <Star className="w-6 h-6 fill-current" />
              </motion.button>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default AIOutputComparison;